import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

/* ---------------------------------------------------------
   Helper: Convert ANY date string into a Date object 
   correctly interpreted in Boston timezone
--------------------------------------------------------- */
function toBostonDate(dateStr) {
  if (!dateStr) return null;

  // Convert input string to YYYY-MM-DD safely
  const [year, month, day] = dateStr.split("-");

  // Create date in Boston timezone (local offset applied)
  return new Date(`${year}-${month}-${day}T00:00:00-05:00`);
}

/* ---------------------------------------------------------
   Long Format — Example: "December 02, 2025"
   👉 Always in Boston timezone
--------------------------------------------------------- */
export const dateFormat = (dateString) => {
 if (!dateString) return "";

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

/* ---------------------------------------------------------
   Short Format — Example: "Dec 02, 2025"
   👉 Always in Boston timezone
--------------------------------------------------------- */
export const dateFormatShort = (dateStr) => {
  const date = toBostonDate(dateStr);
  if (!date) return "";

  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(date);
};

export const formatDateDDMMYYYY = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

/* ---------------------------------------------------------
   Convert "DD-MM-YYYY" → normal JS Date (Boston safe)
--------------------------------------------------------- */
export const formatDDMMYYYYToDayjs = (dateStr) => {
  if (!dateStr) return null;
  const [dd, mm, yyyy] = dateStr.split("-");
  return new Date(`${yyyy}-${mm}-${dd}T00:00:00-05:00`);
};

/* ---------------------------------------------------------
   Convert JS Date → "YYYY-MM-DD"
--------------------------------------------------------- */
export const formatDateToDDMMYYYY = (dateObj) => {
  if (!dateObj) return "";

  const date = new Date(
    dateObj.toLocaleString("en-US", { timeZone: "America/New_York" })
  );

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd}`;
};

/* ---------------------------------------------------------
   Parse time like "01:30 PM" → Boston Date
--------------------------------------------------------- */
export const buildISOTime = (timeStr) => {
  if (!timeStr) return null;

  let [time, mer] = timeStr.split(" ");
  let [hh, mm] = time.split(":");

  hh = Number(hh);
  mm = Number(mm);

  if (mer === "PM" && hh !== 12) hh += 12;
  if (mer === "AM" && hh === 12) hh = 0;

  const today = formatDateToDDMMYYYY(new Date());

  return new Date(`${today}T${hh.toString().padStart(2, "0")}:${mm}:00-05:00`);
};

/**
 * Store hours from API are wall-clock strings (e.g. "06:00 PM", "12:01 AM").
 * Do not use buildISOTime + fixed offset here — it shifts times across DST / time zones
 * and breaks round-trip with MUI TimePicker + dayjs.
 */
export const parseStoreWallClockTime = (timeStr) => {
  if (!timeStr || typeof timeStr !== "string") return null;
  const s = timeStr.trim();
  const d = dayjs(s, ["hh:mm A", "h:mm A", "hh:mmA", "h:mmA"], true);
  return d.isValid() ? d : null;
};

export const convertToMMDDYYYY = (dateStr) => {
  if (!dateStr) return "";

  const [year, month, day] = dateStr.split("-");
  return `${month}-${day}-${year}`;
}